/**
 * @fileoverview Cloudinary Upload Service
 *
 * Handles file uploads to Cloudinary for the RSH Portal.
 * Uses unsigned uploads with a predefined upload preset.
 *
 * Credentials come exclusively from environment variables:
 * - REACT_APP_CLOUDINARY_CLOUD_NAME
 * - REACT_APP_CLOUDINARY_UPLOAD_PRESET
 *
 * Standard folder structure (when projectId/sourceTable/category are supplied):
 *   rsh-portal/{projectId}/{sourceTable}/{category}/
 *
 * Supported file types:
 * - Images: JPG, JPEG, PNG, WEBP, HEIC, GIF
 * - Documents: PDF
 *
 * @version 2.0.0
 */

import { auth } from "../firebase";

const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

if (!CLOUD_NAME || !UPLOAD_PRESET) {
  throw new Error(
    "Missing Cloudinary configuration: REACT_APP_CLOUDINARY_CLOUD_NAME and REACT_APP_CLOUDINARY_UPLOAD_PRESET must be set in the environment.",
  );
}

/**
 * Cloudinary configuration
 * @constant {Object}
 */
const CLOUDINARY_CONFIG = {
  cloudName: CLOUD_NAME,
  uploadPreset: UPLOAD_PRESET,
  folder: 'rsh-portal',
  maxFileSize: 10 * 1024 * 1024, // 10MB in bytes
};

/**
 * Allowed file types and their MIME types
 * @constant {Object}
 */
const ALLOWED_FILE_TYPES = {
  images: {
    mimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/gif'],
    extensions: ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.gif'],
    resourceType: 'image',
  },
  documents: {
    mimeTypes: ['application/pdf'],
    extensions: ['.pdf'],
    resourceType: 'raw',
  },
};

/**
 * Get the Cloudinary upload URL
 * @param {string} resourceType - 'image' or 'raw' (for documents)
 * @returns {string} Upload URL
 */
const getUploadUrl = (resourceType = 'auto') => {
  return `https://api.cloudinary.com/v1_1/${CLOUDINARY_CONFIG.cloudName}/${resourceType}/upload`;
};

/**
 * Escape reserved characters for a Cloudinary context value.
 * Cloudinary context is a `|`-delimited list of `key=value` pairs,
 * so `|` and `=` characters inside values must be backslash-escaped.
 *
 * @param {string} value - Raw value to escape
 * @returns {string} Escaped value safe to embed in a context string
 */
const escapeContextValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/\\/g, "\\\\")
    .replace(/\|/g, "\\|")
    .replace(/=/g, "\\=");
};

/**
 * Build a standardized Cloudinary upload context string and tag list for
 * a file being uploaded from the RSH Portal. The returned `context`
 * string can be appended to FormData as the `context` field; the
 * `tags` array can be joined and appended as the `tags` field.
 *
 * Always includes:
 * - project_id, project_name
 * - category, category_label
 * - source_table
 * - uploaded_by, uploaded_by_name
 * - uploaded_at (ISO 8601 UTC)
 * - original_filename (from the provided File object)
 *
 * `uploaded_by` resolves to the Firebase Auth UID. `uploaded_by_name`
 * prefers an explicit `uploadedByName` override (expected: Airtable
 * Team record name from `useCurrentTeamMember`), then falls back to
 * Firebase Auth `displayName`, `email`, and finally "Unknown".
 *
 * Additional keys may be supplied via `extras`.
 *
 * @param {Object} params - Parameters
 * @param {string} params.projectId - Airtable project record ID
 * @param {string} params.projectName - Human-readable project name
 * @param {string} params.category - Normalized category slug (e.g. "floorplans")
 * @param {string} params.categoryLabel - Human-readable category label (e.g. "Floorplans")
 * @param {("projects"|"files_photos"|"custom_selections"|"tasks"|"change_orders")} params.sourceTable - Airtable table the upload is being written to
 * @param {string} [params.uploadedByName] - Explicit display name for the uploader (preferred: Airtable Team name). Overrides Firebase Auth fallback.
 * @param {Object} [params.extras={}] - Optional additional context keys (e.g. phase, task_id, photo_name)
 * @param {File} params.file - File being uploaded; used for `original_filename`
 * @returns {{ context: string, tags: string[] }} Cloudinary-ready context string and tag array
 *
 * @example
 * const { context, tags } = buildUploadContext({
 *   projectId: "recABC",
 *   projectName: "Smith Residence",
 *   category: "floorplans",
 *   categoryLabel: "Floorplans",
 *   sourceTable: "projects",
 *   file: myFile,
 * });
 * formData.append("context", context);
 * formData.append("tags", tags.join(","));
 */
export const buildUploadContext = ({
  projectId,
  projectName,
  category,
  categoryLabel,
  sourceTable,
  uploadedByName,
  extras = {},
  file,
}) => {
  const user = auth.currentUser;
  const uploadedBy = user?.uid || "";
  const resolvedUploadedByName =
    uploadedByName || user?.displayName || user?.email || "Unknown";

  const pairs = {
    project_id: projectId || "",
    project_name: projectName || "",
    category: category || "",
    category_label: categoryLabel || "",
    source_table: sourceTable || "",
    uploaded_by: uploadedBy,
    uploaded_by_name: resolvedUploadedByName,
    uploaded_at: new Date().toISOString(),
    original_filename: file?.name || "",
    ...extras,
  };

  const context = Object.entries(pairs)
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => `${k}=${escapeContextValue(v)}`)
    .join("|");

  const tags = [
    projectId ? `project:${projectId}` : null,
    category ? `category:${category}` : null,
    sourceTable ? `source:${sourceTable}` : null,
  ].filter(Boolean);

  return { context, tags };
};

/**
 * Validate file before upload
 * @param {File} file - File to validate
 * @param {Object} options - Validation options
 * @param {string[]} [options.allowedTypes] - Array of allowed types: 'images', 'documents', or both
 * @returns {Object} Validation result { valid: boolean, error?: string }
 */
export const validateFile = (file, options = {}) => {
  const { allowedTypes = ['images', 'documents'] } = options;
  
  // Check file size
  if (file.size > CLOUDINARY_CONFIG.maxFileSize) {
    const maxSizeMB = CLOUDINARY_CONFIG.maxFileSize / (1024 * 1024);
    return {
      valid: false,
      error: `File size exceeds ${maxSizeMB}MB limit`,
    };
  }
  
  // Build allowed MIME types based on options
  const allowedMimeTypes = [];
  allowedTypes.forEach(type => {
    if (ALLOWED_FILE_TYPES[type]) {
      allowedMimeTypes.push(...ALLOWED_FILE_TYPES[type].mimeTypes);
    }
  });
  
  // Check file type
  if (!allowedMimeTypes.includes(file.type)) {
    const allowedExtensions = allowedTypes
      .map(type => ALLOWED_FILE_TYPES[type]?.extensions || [])
      .flat()
      .join(', ');
    return {
      valid: false,
      error: `File type not allowed. Accepted: ${allowedExtensions}`,
    };
  }
  
  return { valid: true };
};

/**
 * Determine resource type based on file MIME type
 * @param {File} file - File to check
 * @returns {string} Resource type for Cloudinary ('image' or 'raw')
 */
const getResourceType = (file) => {
  if (ALLOWED_FILE_TYPES.images.mimeTypes.includes(file.type)) {
    return 'image';
  }
  return 'raw';
};

/**
 * Upload a single file to Cloudinary.
 *
 * Two ways to specify the destination folder:
 * 1. **Standardized (preferred):** pass `projectId`, `sourceTable`, and
 *    `category` — the folder is built as
 *    `rsh-portal/{projectId}/{sourceTable}/{category}/`.
 * 2. **Legacy:** pass `folder` as a subpath — the folder is built as
 *    `rsh-portal/{folder}/`. Retained for callers not yet migrated to
 *    the standardized layout.
 *
 * If `uploadContext` (from `buildUploadContext`) is provided, its
 * `context` and `tags` are appended to the Cloudinary upload so the
 * resulting asset carries queryable metadata.
 *
 * @param {File} file - File to upload
 * @param {Object} options - Upload options
 * @param {string} [options.projectId] - Airtable project record ID (standardized folder)
 * @param {("projects"|"files_photos"|"custom_selections"|"tasks"|"change_orders")} [options.sourceTable] - Destination table (standardized folder)
 * @param {string} [options.category] - Normalized category slug (standardized folder)
 * @param {string} [options.folder] - Legacy subfolder within rsh-portal (ignored if projectId+sourceTable+category are provided)
 * @param {{ context: string, tags: string[] }} [options.uploadContext] - Cloudinary context metadata and tags, typically from `buildUploadContext`
 * @param {string} [options.publicId] - Custom public ID for the file
 * @param {string[]} [options.tags] - Additional tags to add to the upload (merged with uploadContext.tags)
 * @param {Function} [options.onProgress] - Progress callback (0-100)
 * @returns {Promise<Object>} Cloudinary response with url, publicId, etc.
 */
export const uploadFile = async (file, options = {}) => {
  const {
    projectId,
    sourceTable,
    category,
    folder = '',
    uploadContext,
    publicId,
    tags = [],
    onProgress,
  } = options;

  // Validate file first
  const validation = validateFile(file, options);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  // Determine resource type
  const resourceType = getResourceType(file);

  // Build form data
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', CLOUDINARY_CONFIG.uploadPreset);

  // Resolve folder path: standardized when projectId+sourceTable+category
  // are all provided, otherwise fall back to the legacy `folder` option.
  let fullFolder;
  if (projectId && sourceTable && category) {
    fullFolder = `${CLOUDINARY_CONFIG.folder}/${projectId}/${sourceTable}/${category}`;
  } else if (folder) {
    fullFolder = `${CLOUDINARY_CONFIG.folder}/${folder}`;
  } else {
    fullFolder = CLOUDINARY_CONFIG.folder;
  }
  formData.append('folder', fullFolder);

  // Optional: custom public ID
  if (publicId) {
    formData.append('public_id', publicId);
  }

  // Attach Cloudinary context metadata if supplied
  if (uploadContext?.context) {
    formData.append('context', uploadContext.context);
  }

  // Merge any context tags with additional caller-supplied tags
  const allTags = [
    ...(uploadContext?.tags || []),
    ...tags,
  ];
  if (allTags.length > 0) {
    formData.append('tags', allTags.join(','));
  }
  
  // Upload with XMLHttpRequest for progress tracking
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    // Track upload progress
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });
    }
    
    // Handle completion
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText);
          resolve({
            success: true,
            url: response.secure_url,
            publicId: response.public_id,
            resourceType: response.resource_type,
            format: response.format,
            width: response.width,
            height: response.height,
            bytes: response.bytes,
            originalFilename: response.original_filename,
            thumbnail: response.eager?.[0]?.secure_url || response.secure_url,
          });
        } catch (error) {
          reject(new Error('Failed to parse upload response'));
        }
      } else {
        try {
          const errorResponse = JSON.parse(xhr.responseText);
          reject(new Error(errorResponse.error?.message || 'Upload failed'));
        } catch {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      }
    });
    
    // Handle errors
    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });
    
    xhr.addEventListener('abort', () => {
      reject(new Error('Upload cancelled'));
    });
    
    // Send request
    xhr.open('POST', getUploadUrl(resourceType));
    xhr.send(formData);
  });
};

/**
 * Upload multiple files to Cloudinary
 * @param {File[]} files - Array of files to upload
 * @param {Object} options - Upload options (same as uploadFile)
 * @param {Function} [options.onFileProgress] - Progress callback per file (fileIndex, progress)
 * @param {Function} [options.onFileComplete] - Callback when each file completes (fileIndex, result)
 * @returns {Promise<Object[]>} Array of Cloudinary responses
 */
export const uploadMultipleFiles = async (files, options = {}) => {
  const { onFileProgress, onFileComplete, ...uploadOptions } = options;
  
  const results = [];
  const errors = [];
  
  for (let i = 0; i < files.length; i++) {
    try {
      const result = await uploadFile(files[i], {
        ...uploadOptions,
        onProgress: (progress) => {
          if (onFileProgress) {
            onFileProgress(i, progress);
          }
        },
      });
      
      results.push(result);
      
      if (onFileComplete) {
        onFileComplete(i, { success: true, result });
      }
    } catch (error) {
      errors.push({ index: i, file: files[i].name, error: error.message });
      
      if (onFileComplete) {
        onFileComplete(i, { success: false, error: error.message });
      }
    }
  }
  
  return {
    successful: results,
    failed: errors,
    allSuccessful: errors.length === 0,
  };
};

/**
 * Generate a Cloudinary thumbnail URL from an existing URL
 * @param {string} url - Original Cloudinary URL
 * @param {Object} options - Transformation options
 * @param {number} [options.width] - Thumbnail width
 * @param {number} [options.height] - Thumbnail height
 * @param {string} [options.crop] - Crop mode (fill, fit, scale, etc.)
 * @returns {string} Transformed URL
 */
export const getThumbnailUrl = (url, options = {}) => {
  const { width = 200, height = 200, crop = 'fill' } = options;
  
  if (!url || !url.includes('cloudinary.com')) {
    return url;
  }
  
  // Insert transformation before the version or file path
  const transformation = `c_${crop},w_${width},h_${height}`;
  
  // Handle different URL formats
  if (url.includes('/upload/')) {
    return url.replace('/upload/', `/upload/${transformation}/`);
  }
  
  return url;
};

/**
 * Get file type category from MIME type
 * @param {string} mimeType - File MIME type
 * @returns {string} Category: 'image', 'pdf', or 'unknown'
 */
export const getFileCategory = (mimeType) => {
  if (ALLOWED_FILE_TYPES.images.mimeTypes.includes(mimeType)) {
    return 'image';
  }
  if (ALLOWED_FILE_TYPES.documents.mimeTypes.includes(mimeType)) {
    return 'pdf';
  }
  return 'unknown';
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size (e.g., "2.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Export config for reference
 */
export const CLOUDINARY_SETTINGS = {
  maxFileSize: CLOUDINARY_CONFIG.maxFileSize,
  maxFileSizeMB: CLOUDINARY_CONFIG.maxFileSize / (1024 * 1024),
  allowedImageTypes: ALLOWED_FILE_TYPES.images.extensions,
  allowedDocumentTypes: ALLOWED_FILE_TYPES.documents.extensions,
};

const cloudinaryService = {
  uploadFile,
  uploadMultipleFiles,
  validateFile,
  getThumbnailUrl,
  getFileCategory,
  formatFileSize,
  CLOUDINARY_SETTINGS,
};

export default cloudinaryService;
